/**
 * Page designer d'invitations + assistant IA
 * Route : /mon-organisation/plan-de-table/invitations
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, Loader2, Copy, Download, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth }               from '../context/AuthContext';
import { useInvitationDesign }   from '../hooks/useInvitationDesign';
import InvitationCard            from '../components/plan-table/InvitationCard';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// ─── Palettes ───────────────────────────────────────────────────────────────
const PALETTES = [
  { label:'Or & Crème',   bg:'#FAF6EE', text:'#3D2F15', accent:'#C9A96E' },
  { label:'Rose poudré',  bg:'#FBEEED', text:'#5A3A39', accent:'#C97A6E' },
  { label:'Sauge & Lin',  bg:'#EEF0E7', text:'#3D4A2F', accent:'#6B8A4E' },
  { label:'Marine & Or',  bg:'#EEF2F5', text:'#1F2E44', accent:'#C9A96E' },
  { label:'Terracotta',   bg:'#F5E6D3', text:'#5C3A26', accent:'#B25B3A' },
  { label:'Lavande',      bg:'#F0EBE1', text:'#3D2F55', accent:'#7A6B8F' },
  { label:'Noir & Or',    bg:'#1A1A1A', text:'#F5E6C8', accent:'#C9A96E' },
  { label:'Eucalyptus',   bg:'#E8EFE9', text:'#2D4435', accent:'#5A8068' },
  { label:'Bordeaux',     bg:'#F5ECEC', text:'#5C1F1F', accent:'#9B3838' },
];

const ORNEMENTS = ['❦ ❦ ❦', '• • •', '✦ ✦ ✦', '⸺', '❀ ❀ ❀', ''];
const THEMES    = [
  { id:'elegant', label:'✨ Élégant' },
  { id:'modern',  label:'🎨 Moderne'  },
  { id:'botanic', label:'🌿 Botanique'},
  { id:'minimal', label:'▫ Minimal'  },
];

export default function InvitationsPage() {
  const { user, token } = useAuth();
  const [planToken, setPlanToken] = useState(null);
  const [plan,      setPlan]      = useState(null);
  const [invites,   setInvites]   = useState([]);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [aiPrompt,      setAiPrompt]      = useState('');
  const [appliedIdx,    setAppliedIdx]    = useState(null);
  const [pdfLoading,    setPdfLoading]    = useState(false);

  const cardRef = useRef(null);

  // ── Récupérer plan token ───────────────────────────────────────────────────
  useEffect(() => {
    if (!user || !token) return;
    fetch(`${BACKEND}/api/mon-plan-de-table`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        const p = d.plans?.[0];
        if (p?.token) {
          setPlanToken(p.token);
          setPlan(p);
          setInvites(p.invites ?? []);
        }
      })
      .catch(() => {});
  }, [user, token]);

  // Si on a le token mais pas les invités complets, re-fetch
  useEffect(() => {
    if (!planToken || !token) return;
    fetch(`${BACKEND}/api/plan-de-table/${planToken}`)
      .then(r => r.json())
      .then(d => {
        setPlan(d.plan);
        setInvites(d.plan?.invites ?? []);
        if (d.plan?.invites?.length > 0) setSelectedGuest(d.plan.invites[0]);
      })
      .catch(() => {});
  }, [planToken, token]);

  const {
    design, loading, updateDesign,
    aiResults, aiLoading, aiError, generateAI, applyAiExample,
  } = useInvitationDesign(planToken, token);

  // ── Invité sélectionné ──────────────────────────────────────────────────
  const currentGuest = selectedGuest ?? invites[0] ?? null;

  // ── Appliquer un exemple IA ────────────────────────────────────────────
  const handleApplyAI = (ex, idx) => {
    applyAiExample(ex);
    setAppliedIdx(idx);
  };

  // ── Copier lien RSVP ──────────────────────────────────────────────────
  const copyRsvpLink = useCallback(() => {
    if (!currentGuest?.rsvp_token) {
      toast.error('Sélectionnez un invité d\'abord');
      return;
    }
    const url = `${window.location.origin}/rsvp/${currentGuest.rsvp_token}`;
    navigator.clipboard.writeText(url)
      .then(() => toast.success(`✓ Lien copié pour ${currentGuest.nom}`))
      .catch(() => { prompt('Copiez ce lien :', url); });
  }, [currentGuest]);

  // ── Générer PDF toutes invitations ─────────────────────────────────────
  const downloadAllPDF = useCallback(async () => {
    if (invites.length === 0) { toast.error('Aucun invité'); return; }
    setPdfLoading(true);
    try {
      const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
        import('jspdf'),
        import('html2canvas'),
      ]);
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const prevGuest = selectedGuest;

      for (let i = 0; i < invites.length; i++) {
        setSelectedGuest(invites[i]);
        await new Promise(r => setTimeout(r, 80));

        if (!cardRef.current) continue;
        const canvas = await html2canvas(cardRef.current, {
          scale: 2, backgroundColor: null, useCORS: true, logging: false,
        });
        const imgData = canvas.toDataURL('image/jpeg', 0.92);
        const pageW   = 180; // mm
        const pageH   = (canvas.height / canvas.width) * pageW;
        const xOff    = 15;
        const yOff    = (297 - pageH) / 2;

        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, 'JPEG', xOff, yOff, pageW, pageH);
      }

      const slug = (plan?.nom_evenement || 'invitations')
        .toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-');
      pdf.save(`invitations-${slug}.pdf`);
      toast.success(`✓ ${invites.length} invitation${invites.length > 1 ? 's' : ''} téléchargée${invites.length > 1 ? 's' : ''}`);
      setSelectedGuest(prevGuest);
    } catch (e) {
      toast.error('Erreur PDF : ' + e.message);
    } finally {
      setPdfLoading(false);
    }
  }, [invites, selectedGuest, plan, cardRef]);

  if (!user) return (
    <div style={{ padding:40, textAlign:'center', fontFamily:"'Montserrat',sans-serif" }}>
      <Link to="/connexion" style={{ color:'#D4AF37' }}>Se connecter</Link>
    </div>
  );

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100vh', overflow:'hidden',
      fontFamily:"'Montserrat',sans-serif" }}>

      {/* Nav */}
      <div style={{ height:48, flexShrink:0, background:'#0B1E20', borderBottom:'2px solid #D4AF37',
        display:'flex', alignItems:'center', gap:12, padding:'0 20px' }}>
        <Link to="/mon-organisation/plan-de-table/editeur"
          style={{ color:'#D4AF37', display:'flex', alignItems:'center', gap:4,
            textDecoration:'none', fontSize:12, fontWeight:600 }}>
          <ArrowLeft size={14} /> Éditeur
        </Link>
        <span style={{ color:'rgba(255,255,255,.3)' }}>|</span>
        <span style={{ color:'#FFFFFF', fontSize:13, fontWeight:700 }}>
          Invitations — {plan?.nom_evenement || '…'}
        </span>
      </div>

      {/* Body 2 colonnes */}
      <div style={{ flex:1, display:'flex', overflow:'hidden' }} className="inv-layout">

        {/* ═══ PANNEAU GAUCHE ═════════════════════════════════════════════ */}
        <div style={{ width:360, flexShrink:0, borderRight:'1px solid #E5E7EB',
          overflowY:'auto', display:'flex', flexDirection:'column',
          background:'#FFFFFF' }}>

          <div style={{ padding:'14px 16px', flex:1 }}>

            {/* Section 1 — Assistant IA */}
            <section style={{ marginBottom:18, padding:14,
              background:'linear-gradient(135deg,rgba(212,175,55,.05),rgba(93,188,191,.05))',
              border:'1.5px solid rgba(212,175,55,.3)', borderRadius:10 }}>
              <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:6 }}>
                <Sparkles size={13} style={{ color:'#D4AF37' }} />
                <span style={{ fontSize:10, fontWeight:800, textTransform:'uppercase',
                  letterSpacing:'0.1em', color:'#D4AF37' }}>Assistant IA</span>
              </div>
              <p style={{ fontSize:11, color:'#6B7280', marginBottom:10, lineHeight:1.5 }}>
                Décris l'ambiance souhaitée — l'IA propose 3 styles complets
              </p>
              <textarea
                value={aiPrompt}
                onChange={e => setAiPrompt(e.target.value)}
                rows={3}
                placeholder="Ex : Mariage bohème en Provence, ambiance chaleureuse fin août, tons terracotta et olive…"
                style={{ ...taStyle, marginBottom:8 }}
              />
              <button
                onClick={() => generateAI(aiPrompt)}
                disabled={aiLoading || !aiPrompt.trim()}
                style={{ ...goldBtn, width:'100%', display:'flex', alignItems:'center',
                  justifyContent:'center', gap:6,
                  opacity: (aiLoading || !aiPrompt.trim()) ? 0.5 : 1,
                  cursor: aiLoading || !aiPrompt.trim() ? 'not-allowed' : 'pointer' }}
              >
                {aiLoading
                  ? <><Loader2 size={13} className="animate-spin" /> Génération…</>
                  : <><Sparkles size={13} /> Générer des idées</>}
              </button>
            </section>

            {/* Section 2 — Résultats IA */}
            {aiError && (
              <div style={{ display:'flex', gap:8, padding:'10px 12px', borderRadius:8, marginBottom:14,
                background:'rgba(239,68,68,.06)', border:'1px solid rgba(239,68,68,.2)' }}>
                <AlertCircle size={14} style={{ color:'#EF4444', flexShrink:0, marginTop:1 }} />
                <p style={{ fontSize:11, color:'#EF4444', lineHeight:1.5, margin:0 }}>{aiError}</p>
              </div>
            )}
            {aiResults && aiResults.length > 0 && (
              <div style={{ marginBottom:18 }}>
                <p style={sectionLabel}>Suggestions IA</p>
                {aiResults.map((ex, i) => (
                  <div key={i}
                    onClick={() => handleApplyAI(ex, i)}
                    style={{
                      border: `2px solid ${appliedIdx === i ? '#22C55E' : '#E5E7EB'}`,
                      borderRadius:10, overflow:'hidden', marginBottom:8, cursor:'pointer',
                      transition:'transform .15s, box-shadow .15s',
                      transform: appliedIdx === i ? 'scale(1.01)' : 'scale(1)',
                      boxShadow: appliedIdx === i ? '0 4px 16px rgba(34,197,94,.2)' : '0 1px 4px rgba(0,0,0,.06)',
                    }}>
                    {/* Swatches */}
                    <div style={{ display:'flex', height:20 }}>
                      {[ex.bg, ex.text, ex.accent].map((c,j) => (
                        <div key={j} style={{ flex:1, background:c }} />
                      ))}
                    </div>
                    <div style={{ padding:'8px 10px' }}>
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:2 }}>
                        <span style={{ fontFamily:"'Playfair Display',serif", fontWeight:700, fontSize:13 }}>
                          {ex.title}
                        </span>
                        {appliedIdx === i && (
                          <span style={{ fontSize:9, fontWeight:700, color:'#22C55E',
                            background:'rgba(34,197,94,.1)', padding:'1px 6px', borderRadius:8 }}>
                            ✓ Appliqué
                          </span>
                        )}
                      </div>
                      <p style={{ fontSize:11, fontStyle:'italic', color:'#6B7280', margin:'0 0 6px' }}>
                        {ex.description}
                      </p>
                      <div style={{ fontStyle:'italic', fontSize:10, borderLeft:'2px solid #D4AF37',
                        paddingLeft:8, color:'#374151', lineHeight:1.5, marginBottom:6,
                        background:'rgba(212,175,55,.04)' }}>
                        {ex.message?.slice(0, 80)}…
                      </div>
                      <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
                        <span style={badgeStyle}>{ex.theme}</span>
                        {ex.dress_code    && <span style={badgeStyle}>👔 {ex.dress_code.slice(0,20)}</span>}
                        {ex.rsvp_deadline && <span style={badgeStyle}>📅 {ex.rsvp_deadline}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Section 3 — Thème */}
            <section style={{ marginBottom:14 }}>
              <p style={sectionLabel}>Thème</p>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6 }}>
                {THEMES.map(t => (
                  <button key={t.id} onClick={() => updateDesign({ theme: t.id })}
                    style={{ padding:'7px 8px', borderRadius:7, fontSize:11, fontWeight:600,
                      cursor:'pointer', border:`1.5px solid ${design.theme===t.id ? '#D4AF37' : '#E5E7EB'}`,
                      background: design.theme===t.id ? 'rgba(212,175,55,.1)' : '#F9FAFB',
                      color: design.theme===t.id ? '#C58B19' : '#374151' }}>
                    {t.label}
                  </button>
                ))}
              </div>
            </section>

            {/* Section 4 — Palettes */}
            <section style={{ marginBottom:14 }}>
              <p style={sectionLabel}>Palettes prêtes</p>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:6 }}>
                {PALETTES.map(p => (
                  <button key={p.label} title={p.label}
                    onClick={() => updateDesign({ bg_color: p.bg, text_color: p.text })}
                    style={{ borderRadius:8, overflow:'hidden', border:'1.5px solid #E5E7EB',
                      cursor:'pointer', padding:0,
                      boxShadow: design.bg_color === p.bg ? `0 0 0 2px #D4AF37` : 'none' }}>
                    <div style={{ display:'flex', height:16 }}>
                      {[p.bg, p.text, p.accent].map((c,i) => (
                        <div key={i} style={{ flex:1, background:c }} />
                      ))}
                    </div>
                    <p style={{ fontSize:9, fontWeight:600, color:'#6B7280',
                      padding:'2px 4px', margin:0, textAlign:'center',
                      background:'#F9FAFB', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                      {p.label}
                    </p>
                  </button>
                ))}
              </div>
            </section>

            {/* Section 5 — Couleurs personnalisées */}
            <section style={{ marginBottom:14 }}>
              <p style={sectionLabel}>Couleurs personnalisées</p>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                {[
                  { label:'Fond',          key:'bg_color'   },
                  { label:'Texte / Accent',key:'text_color' },
                ].map(f => (
                  <div key={f.key}>
                    <label style={{ fontSize:10, fontWeight:600, color:'#6B7280', display:'block', marginBottom:3 }}>
                      {f.label}
                    </label>
                    <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                      <input type="color" value={design[f.key] || '#FAF6EE'}
                        onChange={e => updateDesign({ [f.key]: e.target.value })}
                        style={{ width:28, height:28, borderRadius:4, border:'1px solid #D1D5DB',
                          padding:1, cursor:'pointer' }} />
                      <input type="text" value={design[f.key] || ''}
                        onChange={e => { if (/^#[0-9A-Fa-f]{0,6}$/.test(e.target.value)) updateDesign({ [f.key]: e.target.value }); }}
                        style={{ flex:1, padding:'4px 6px', borderRadius:5, fontSize:10,
                          border:'1px solid #D1D5DB', outline:'none', fontFamily:'monospace' }} />
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={() => updateDesign({ bg_color:'#FAF6EE', text_color:'#3D2F15' })}
                style={{ marginTop:6, fontSize:10, color:'#6B7280', background:'none',
                  border:'1px solid #E5E7EB', borderRadius:5, padding:'3px 8px', cursor:'pointer' }}>
                ↺ Réinitialiser les couleurs
              </button>
            </section>

            {/* Section 6 — Aperçu pour */}
            <section style={{ marginBottom:14 }}>
              <p style={sectionLabel}>Aperçu pour</p>
              <Select
                value={currentGuest?.id ?? '__generic__'}
                onValueChange={v => setSelectedGuest(v === '__generic__' ? null : invites.find(i => i.id === v) ?? null)}
              >
                <SelectTrigger style={{ fontSize:12, height:32 }}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__generic__">— Aperçu générique —</SelectItem>
                  {invites.map(inv => (
                    <SelectItem key={inv.id} value={inv.id}>{inv.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </section>

            {/* Section 7 — Message */}
            <section style={{ marginBottom:14 }}>
              <p style={sectionLabel}>Message</p>
              <textarea rows={3} value={design.message ?? ''}
                onChange={e => updateDesign({ message: e.target.value })}
                placeholder="Nous serions honorés de votre présence…"
                style={taStyle} />
            </section>

            {/* Section 8 — Ornement */}
            <section style={{ marginBottom:14 }}>
              <p style={sectionLabel}>Ornement</p>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:5 }}>
                {ORNEMENTS.map(o => (
                  <button key={o || 'none'}
                    onClick={() => updateDesign({ ornament: o })}
                    style={{ padding:'6px 4px', borderRadius:6, fontSize: o ? 14 : 11,
                      cursor:'pointer', letterSpacing:'0.1em', fontWeight: o ? 400 : 600,
                      border:`1.5px solid ${design.ornament === o ? '#D4AF37' : '#E5E7EB'}`,
                      background: design.ornament === o ? 'rgba(212,175,55,.1)' : '#F9FAFB',
                      color: design.ornament === o ? '#C58B19' : (o ? '#374151' : '#9CA3AF') }}>
                    {o || 'Aucun'}
                  </button>
                ))}
              </div>
            </section>

            {/* Section 9 — Champs supplémentaires */}
            <section style={{ marginBottom:14 }}>
              <p style={sectionLabel}>Champs supplémentaires</p>
              {[
                { label:'Dress code',     key:'dress_code',    placeholder:'Ex : Tenue de cocktail' },
                { label:'Date limite RSVP',key:'rsvp_deadline',placeholder:'Ex : Avant le 15 mai' },
                { label:'Adresse',        key:'address',       placeholder:'Ex : Domaine de Figeac, 46100' },
                { label:'Contact',        key:'contact',       placeholder:'Ex : 06 12 34 56 78' },
              ].map(f => (
                <div key={f.key} style={{ marginBottom:7 }}>
                  <label style={{ fontSize:10, fontWeight:600, color:'#6B7280', display:'block', marginBottom:2 }}>
                    {f.label}
                  </label>
                  <input value={design[f.key] ?? ''} placeholder={f.placeholder}
                    onChange={e => updateDesign({ [f.key]: e.target.value })}
                    style={{ ...inputStyle, padding:'5px 8px' }} />
                </div>
              ))}
            </section>
          </div>

          {/* Section 10 — Actions sticky bottom */}
          <div style={{ padding:'12px 16px', borderTop:'1px solid #F3F4F6',
            background:'#FFFFFF', flexShrink:0 }}>
            <button onClick={downloadAllPDF} disabled={pdfLoading}
              style={{ ...goldBtn, width:'100%', display:'flex', alignItems:'center',
                justifyContent:'center', gap:6, marginBottom:8,
                opacity: pdfLoading ? 0.7 : 1, cursor: pdfLoading ? 'not-allowed' : 'pointer' }}>
              {pdfLoading
                ? <><Loader2 size={14} className="animate-spin" /> Génération en cours…</>
                : <><Download size={14} /> Télécharger toutes les invitations (PDF)</>}
            </button>
            <button onClick={copyRsvpLink}
              style={{ width:'100%', padding:'8px', borderRadius:8, border:'1.5px solid #5DBCBF',
                background:'transparent', color:'#0B4B4E', fontSize:12, fontWeight:600,
                cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
                gap:6, marginBottom:10 }}>
              <Copy size={13} /> Copier lien RSVP de l'invité sélectionné
            </button>
            <div style={{ padding:'8px 10px', background:'#F9FAFB', borderRadius:7,
              fontSize:10, color:'#9CA3AF', lineHeight:1.6 }}>
              💡 Chaque invité a un lien RSVP unique. Envoyez-leur par email/SMS/WhatsApp,
              ils confirment en un clic.
            </div>
          </div>
        </div>

        {/* ═══ PANNEAU DROIT — Aperçu ══════════════════════════════════════ */}
        <div style={{ flex:1, overflow:'auto', display:'flex', alignItems:'center',
          justifyContent:'center', padding:40,
          background:'#F0EBE1',
          backgroundImage:'radial-gradient(circle,rgba(0,0,0,.03) 1px,transparent 1px)',
          backgroundSize:'24px 24px' }}>
          {loading ? (
            <Loader2 size={28} className="animate-spin" style={{ color:'#D4AF37' }} />
          ) : (
            <InvitationCard
              ref={cardRef}
              design={design}
              plan={plan}
              guestName={currentGuest?.nom}
              rsvpToken={currentGuest?.rsvp_token}
            />
          )}
        </div>
      </div>

      {/* Responsive CSS */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .animate-spin  { animation: spin 1s linear infinite; }
        @media (max-width: 1100px) {
          .inv-layout { flex-direction: column !important; overflow-y: auto !important; }
          .inv-layout > div:first-child {
            width: 100% !important; max-height: 50vh; border-right: none !important;
            border-bottom: 1px solid #E5E7EB !important;
          }
          .inv-layout > div:last-child { min-height: 400px; }
        }
      `}</style>
    </div>
  );
}

const sectionLabel = { fontSize:10, fontWeight:800, textTransform:'uppercase',
  letterSpacing:'0.08em', color:'#9CA3AF', marginBottom:7, marginTop:0 };
const taStyle = { width:'100%', padding:'7px 9px', borderRadius:7, fontSize:11,
  border:'1px solid #D1D5DB', outline:'none', resize:'vertical', boxSizing:'border-box',
  fontFamily:"'Montserrat',sans-serif", lineHeight:1.6 };
const inputStyle = { width:'100%', borderRadius:6, fontSize:11, boxSizing:'border-box',
  border:'1px solid #D1D5DB', outline:'none', fontFamily:"'Montserrat',sans-serif" };
const goldBtn    = { padding:'9px 16px', borderRadius:8, border:'none', background:'#D4AF37',
  color:'#0B1E20', fontWeight:700, fontSize:12, cursor:'pointer',
  fontFamily:"'Montserrat',sans-serif" };
const badgeStyle = { fontSize:9, fontWeight:600, padding:'2px 6px', borderRadius:8,
  background:'#F3F4F6', color:'#6B7280' };
